import React, { Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import get from 'lodash.get';

/* Helpers */
import { getProductTileAdMap } from '../../utils/adHelpers';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import {
    showFreeShippingTest,
    trackFreeShippingTest,
} from '../SbRespTradeFreeShippingVariant/tradeFreeShippingTestHelper';

/* Components */
import { TradeRewardsMessagingWrapper } from '../../components/global/TradeRewardsMessagingWrapper';
import { ProductTileAd } from '../../components/global/Ads/ProductTileAd';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { SbSharedItemTrackingProvider } from '../SbSharedItemTrackingProvider/SbSharedItemTrackingProvider';
import { SbSharedMeasurementUnitProvider } from '../SbSharedMeasurementUnitProvider/SbSharedMeasurementUnitProvider';
import { SbRespSearchResultGrid, PROMINENT_TILE } from './SbRespSearchResultGrid';
import { SbRespProminentTileDescription } from '../SbRespProminentTileDescription/SbRespProminentTileDescription';
import { SbRespTradeSourcingBanner } from '../SbRespTradeSourcingBanner/SbRespTradeSourcingBanner';

import styles from './sbRespSearchResultContainer.scss';

import { SbRespVisuallySimilarLink } from '../SbRespVisuallySimilarLink/SbRespVisuallySimilarLink';
import { SbRespNegotiable } from '../SbRespNegotiable/SbRespNegotiable';

/* Constants */
import { VERTICAL_OFFSET_SB_RESP_IMG } from '../../constants/imageConstants';

const ITEMS_PER_ROW = 3;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 4;
const TRADE_SOURCING_BANNER_POSITION = ITEMS_PER_ROW * 3; // 4th row

const SbRespSearchResultContainerComponent = props => {
    const {
        currency,
        showMeasurements,
        showInternalSellerName,
        showSellerName,
        showCreatorName,
        itemSearch,
        viewer,
        relay,
        isClient,
        user,
        renderVariables,
        pageSize,
        gridType,
        semanticSearchTerm,
        hasCheckedForSemanticSearchTerm,
    } = props;

    const { edges: items = [], displayUriRef } = itemSearch || {};
    const {
        isTileAdEnabled,
        isFavoritesEnabled,
        isSeoQuicklookEnabled,
        isVisuallySimilarEnabled,
    } = viewer;

    const productTileAdMap = getProductTileAdMap({
        uriRef: displayUriRef,
        pageSize,
    });

    const userId = user ? user.serviceId : null;
    const isVerifiedTrade = get(user, 'isVerifiedTrade');

    const trackTradeFreeShippingTest =
        isVerifiedTrade && !get(viewer, 'isSbTradeFreeShippingTestDisabled');
    const showTradeFreeShippingTest = trackTradeFreeShippingTest && showFreeShippingTest();
    useEffect(() => {
        if (trackTradeFreeShippingTest) {
            trackFreeShippingTest();
        }
    }, [trackTradeFreeShippingTest]);

    /* eslint-disable react/prop-types, react/display-name */
    return (
        <div className={styles.container}>
            <FavoritesProvider
                disable={!isClient}
                environment={relay.environment}
                itemIds={items.map(({ node }) => get(node, 'item.serviceId'))}
                location={locations.SEARCH_BROWSE}
                userId={userId}
                renderVariables={renderVariables}
            >
                {({ props: favorites }) => (
                    <QuickViewProvider>
                        {({ quickView }) => (
                            <SbSharedItemTrackingProvider
                                itemSearch={itemSearch}
                                semanticSearchTerm={semanticSearchTerm}
                                hasCheckedForSemanticSearchTerm={hasCheckedForSemanticSearchTerm}
                            >
                                {({ fireItemImpressionTracking, fireItemClickTracking }) => (
                                    <SbRespSearchResultGrid items={items} type={gridType}>
                                        {({ item, index, customTileWidthClass, tileType }) => {
                                            const ad = productTileAdMap[index + 1]; // 1-indexed
                                            const quickViewProps = isSeoQuicklookEnabled
                                                ? quickView
                                                : null;
                                            let adEl;

                                            if (ad) {
                                                const { adId, elementId } = ad;
                                                if (get(user, 'tradeFirm.serviceId')) {
                                                    adEl = (
                                                        <TradeRewardsMessagingWrapper
                                                            environment={relay.environment}
                                                            user={user}
                                                        />
                                                    );
                                                } else {
                                                    adEl = (
                                                        <ProductTileAd
                                                            adId={adId}
                                                            elementId={elementId}
                                                        />
                                                    );
                                                }
                                            }

                                            const itemId = get(item, 'serviceId') || '';
                                            const isProminentTile = tileType === PROMINENT_TILE;

                                            const children = ({ tileHoveredClass }) => (
                                                <Fragment>
                                                    {!isVerifiedTrade && (
                                                        <SbRespNegotiable item={item} />
                                                    )}

                                                    {isProminentTile && (
                                                        <SbRespProminentTileDescription
                                                            item={item}
                                                        />
                                                    )}

                                                    {isVisuallySimilarEnabled && (
                                                        <div
                                                            className={`${styles.visuallySimilarWrapper} ${tileHoveredClass}`}
                                                        >
                                                            <SbRespVisuallySimilarLink
                                                                itemId={itemId}
                                                                isClient={!!isClient}
                                                            />
                                                        </div>
                                                    )}
                                                </Fragment>
                                            );
                                            const tileImageSize = isProminentTile
                                                ? 'large'
                                                : undefined;

                                            const isFreeShippingToBuyer = !!get(
                                                item,
                                                'lowestShipmentQuote[0].isFreeToBuyer'
                                            );

                                            return (
                                                <Fragment>
                                                    {index === TRADE_SOURCING_BANNER_POSITION && (
                                                        <SbRespTradeSourcingBanner
                                                            itemSearch={itemSearch}
                                                            user={user}
                                                        />
                                                    )}
                                                    {isTileAdEnabled && adEl}
                                                    <SbSharedMeasurementUnitProvider
                                                        user={user}
                                                        itemSearch={itemSearch}
                                                    >
                                                        {({ measurementUnit }) => (
                                                            <SharedItemTile
                                                                item={item}
                                                                favorites={
                                                                    isFavoritesEnabled && favorites
                                                                }
                                                                index={index}
                                                                currency={currency}
                                                                isMobile={false}
                                                                showInternalSellerName={
                                                                    showInternalSellerName
                                                                }
                                                                showSellerName={showSellerName}
                                                                showCreatorName={showCreatorName}
                                                                showMeasurements={showMeasurements}
                                                                quickViewProps={quickViewProps}
                                                                children={children}
                                                                lazyLoadStartIndex={
                                                                    LAZY_LOAD_ITEM_TILE_INDEX
                                                                }
                                                                imageLoadVerticalOffset={
                                                                    VERTICAL_OFFSET_SB_RESP_IMG
                                                                }
                                                                onContentVisible={() =>
                                                                    fireItemImpressionTracking(
                                                                        index
                                                                    )
                                                                }
                                                                onClick={() =>
                                                                    fireItemClickTracking(index)
                                                                }
                                                                measurementUnit={measurementUnit}
                                                                customTileWidthClass={
                                                                    customTileWidthClass
                                                                }
                                                                imageSize={tileImageSize}
                                                                showShippingBadge={
                                                                    isFreeShippingToBuyer &&
                                                                    showTradeFreeShippingTest
                                                                }
                                                            />
                                                        )}
                                                    </SbSharedMeasurementUnitProvider>
                                                </Fragment>
                                            );
                                        }}
                                    </SbRespSearchResultGrid>
                                )}
                            </SbSharedItemTrackingProvider>
                        )}
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
    /* eslint-enable react/prop-types, react/display-name */
};

SbRespSearchResultContainerComponent.propTypes = {
    currency: PropTypes.string.isRequired,
    showMeasurements: PropTypes.bool.isRequired,
    showInternalSellerName: PropTypes.bool,
    showSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    itemSearch: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    isClient: PropTypes.bool,
    user: PropTypes.object,
    pageSize: PropTypes.number.isRequired,
    renderVariables: PropTypes.object.isRequired,
    gridType: PropTypes.string,
    semanticSearchTerm: PropTypes.string,
    hasCheckedForSemanticSearchTerm: PropTypes.bool,
};

function mapStateToProps(state) {
    return {
        renderVariables: get(state, 'relayVariables.variables'),
        pageSize: get(state, 'relayVariables.variables.first'),
        semanticSearchTerm: get(state, 'filters.semanticSearchTerm'),
        hasCheckedForSemanticSearchTerm: get(state, 'filters.hasCheckedForSemanticSearchTerm'),
    };
}

export const SbRespSearchResultContainer = createFragmentContainer(
    connect(mapStateToProps)(SbRespSearchResultContainerComponent),
    {
        viewer: graphql`
            fragment SbRespSearchResultContainer_viewer on Viewer {
                isSeoQuicklookEnabled: isEnabledByLocale(featureName: "SEO_QUICKLOOK")
                isTileAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isFavoritesEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isVisuallySimilarEnabled: isEnabledByLocale(featureName: "VISUALLY_SIMILAR")
                isSbTradeFreeShippingTestDisabled: isEnabled(key: "disableSbTradeFreeShippingTest")
                    @include(if: $isTrade)
            }
        `,
        user: graphql`
            fragment SbRespSearchResultContainer_user on User {
                serviceId
                isVerifiedTrade
                tradeFirm @include(if: $isTrade) {
                    serviceId
                }
                ...SbSharedMeasurementUnitProvider_user
                ...TradeRewardsMessagingWrapper_user
                ...SbRespTradeSourcingBanner_user
            }
        `,
        itemSearch: graphql`
            fragment SbRespSearchResultContainer_itemSearch on ItemSearchQueryConnection {
                ...SbSharedMeasurementUnitProvider_itemSearch
                ...SbSharedItemTrackingProvider_itemSearch
                ...SbRespTradeSourcingBanner_itemSearch
                displayUriRef
                edges {
                    node {
                        item {
                            serviceId
                            ...SbRespProminentTileDescription_item
                            ...SharedItemTile_item
                            ...SbRespNegotiable_item
                            lowestShipmentQuote @include(if: $isTrade) {
                                isFreeToBuyer
                            }
                        }
                    }
                }
            }
        `,
    }
);
